import React, { useEffect, useRef, useState, useCallback } from "react";
import type { Decision, DecisionChoice, RatifyEntry, ChainLink } from "../types";
import Rel from "../atoms/Rel";
import { parseOptionsFromXml, parseOptionsFromJson, parseStepsFromXml } from "../lib/bodyParser";
import type { ParsedOption, ParsedStep } from "../lib/bodyParser";
import "./PanelDecisions.css";

type DecisionRowProps = {
  d: Decision;
  expanded: boolean;
  onToggle: () => void;
  onResolve: (d: Decision, key: string, text?: string) => void;
  /** Fetches the row body from the API (sub-agent C contract:
   *  GET /api/decisions/{id}?include_body=true).
   *  Optional: when absent, body rendering falls back to legacy choices[]. */
  fetchDecisionBody?: (id: string) => Promise<Decision | null>;
  /** BOARDROOM-2026-08-08-124017 step 2: one-click dismiss, no reason
   *  required. Duncan must never be unable to clear a card — this is
   *  always rendered (not gated on stale), calling
   *  POST /api/cards/{ref}/dismiss. */
  onDismiss?: (d: Decision) => void;
};

// ---------------------------------------------------------------------------
// WU-08: Previous-decisions + Chain-trail sub-components
// ---------------------------------------------------------------------------

/** Render the "Previous decisions" section from ratify_history.
 * Hidden when ratify_history is empty or missing (M5/M12). */
function PreviousDecisions({ history }: { history: RatifyEntry[] }) {
  if (!history || history.length === 0) return null;
  return (
    <div className="drow-prev-decisions" data-testid="drow-prev-decisions">
      <div className="drow-detail-head">Previous decisions</div>
      <ul className="drow-prev-list">
        {history.map((entry) => (
          <li key={entry.ref} className="drow-prev-row">
            <code className="drow-prev-ref">{entry.ref}</code>
            <span className="drow-prev-key drow-ch-k">{entry.chosen_key}</span>
            {entry.chosen_other_text && (
              <span className="drow-prev-other" title={entry.chosen_other_text}>
                {entry.chosen_other_text}
              </span>
            )}
            <span className="drow-prev-when">
              <Rel t={entry.decided_at ? new Date(entry.decided_at).getTime() : 0} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Render the "Chain trail" section from chain_links.
 * Hidden when chain_links is empty or missing (M9). */
function ChainTrail({ links }: { links: ChainLink[] }) {
  if (!links || links.length === 0) return null;
  return (
    <div className="drow-chain-trail" data-testid="drow-chain-trail">
      <div className="drow-detail-head">Chain trail</div>
      <ul className="drow-chain-list">
        {links.map((link, i) => (
          <li key={`${link.kind}-${link.ref || i}`} className="drow-chain-row">
            {link.kind === "correction" ? (
              <span className="drow-chain-correction">
                <span className="drow-chain-kind">v{link.from_version}→v{link.to_version}</span>
                {link.summary && (
                  <span className="drow-chain-summary"> {link.summary}</span>
                )}
              </span>
            ) : (
              <span className="drow-chain-link">
                <span className="drow-chain-kind">{link.kind}</span>
                {link.ref && <code className="drow-chain-ref">{link.ref}</code>}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Render the OTHER follow-up inline context when a card's ratify_history
 * contains an OTHER entry and the card itself supersedes a prior card (M10).
 * Hidden when the most-recent prior ratify was not OTHER (M11). */
function OtherFollowupContext({ history }: { history: RatifyEntry[] }) {
  if (!history || history.length === 0) return null;
  // Surface the most-recent Other entry from the history (if any).
  const otherEntries = history.filter(
    (e) => e.chosen_key?.toUpperCase() === "OTHER" && e.chosen_other_text,
  );
  if (otherEntries.length === 0) return null;
  const latest = otherEntries[otherEntries.length - 1];
  return (
    <div className="drow-other-followup" data-testid="drow-other-followup">
      <span className="drow-other-followup-label">
        Following up on previous OTHER decision:
      </span>
      <span className="drow-other-followup-text">{latest.chosen_other_text}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// S70: Rich body renderers — render-by-subtype (BOARDROOM-2026-05-05-071740-480)
// ---------------------------------------------------------------------------

/** Render a single option card with title, text, pros, cons, and a Choose
 *  button. The Choose button fires onChoose(option.n) — wired to the existing
 *  pick() flow so ratify semantics are unchanged. */
function OptionCard({
  option,
  recommended,
  onChoose,
  submitting,
}: {
  option: ParsedOption;
  recommended: string;
  onChoose: (key: string) => void;
  submitting: string | null;
}) {
  const isRec =
    recommended === option.n || recommended === option.label;
  return (
    <div
      className={`drow-body-option ${isRec ? "is-rec" : ""}`}
      data-testid={`option-card-${option.n}`}
    >
      <div className="drow-body-option-head">
        <span className="drow-body-option-n">{option.n}</span>
        <span className="drow-body-option-title">{option.title}</span>
        {isRec && <span className="drow-ch-tag">board rec</span>}
        <button
          type="button"
          className={`drow-body-option-choose ${submitting === option.n ? "is-busy" : ""}`}
          onClick={() => onChoose(option.n)}
          disabled={!!submitting}
          aria-label={`Choose option ${option.n}: ${option.title}`}
        >
          {submitting === option.n ? "choosing…" : "Choose"}
        </button>
      </div>
      {option.text && (
        <div className="drow-body-option-text">{option.text}</div>
      )}
      {option.pros.length > 0 && (
        <div className="drow-body-option-pros" data-testid={`option-pros-${option.n}`}>
          <span className="drow-body-proscons-label">Pros</span>
          <ul>
            {option.pros.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </div>
      )}
      {option.cons.length > 0 && (
        <div className="drow-body-option-cons" data-testid={`option-cons-${option.n}`}>
          <span className="drow-body-proscons-label">Cons</span>
          <ul>
            {option.cons.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Render option cards parsed from row body_xml / body_json.
 *  Each card: title + text + pros (bullet) + cons (bullet) + Choose button. */
function BodyOptionsView({
  options,
  recommended,
  onChoose,
  submitting,
}: {
  options: ParsedOption[];
  recommended: string;
  onChoose: (key: string) => void;
  submitting: string | null;
}) {
  if (options.length === 0) return null;
  return (
    <div className="drow-body-options" data-testid="drow-body-options">
      <div className="drow-detail-head">Options</div>
      {options.map((opt) => (
        <OptionCard
          key={opt.n}
          option={opt}
          recommended={recommended}
          onChoose={onChoose}
          submitting={submitting}
        />
      ))}
    </div>
  );
}

/** Render action steps from a row body (action-card subtype).
 *  Numbered list matching <step n> or <action_step n> tags. */
function BodyStepsView({ steps }: { steps: ParsedStep[] }) {
  if (steps.length === 0) return null;
  return (
    <div className="drow-body-steps" data-testid="drow-body-steps">
      <div className="drow-detail-head">Steps</div>
      <ol className="drow-body-steps-list">
        {steps.map((s) => (
          <li key={s.n} data-testid={`body-step-${s.n}`}>{s.body}</li>
        ))}
      </ol>
    </div>
  );
}

// ---------------------------------------------------------------------------
// S70: _renderBodyContent — determines what to show in the expanded detail
// section based on row type and available body data.
// ---------------------------------------------------------------------------

type BodyFetchState = "idle" | "loading" | "done" | "unavailable";

/**
 * Render the expanded body section for a DecisionRow.
 *
 * Branching logic (BOARDROOM-2026-05-05-071740-480 ac n=7):
 * 1. action-card type → steps from body_xml (BodyStepsView)
 * 2. option-bearing type (msg-escalation, decision-card, default) → rich option cards
 *    from body_xml/body_json (BodyOptionsView), falling back to legacy choices[]
 * 3. If body fetch is pending → loading state
 * 4. If body unavailable (API not yet deployed) → legacy choices[] with pending notice
 */
function _renderBodyContent(
  d: Decision,
  bodyRow: Decision | null,
  bodyFetchState: BodyFetchState,
  onChoose: (key: string) => void,
  submitting: string | null,
): React.ReactNode {
  // Determine effective type: prefer explicit `type`, fall back to `subtype`.
  const rowType = (d.type || d.subtype || "").toLowerCase();
  const isActionCard =
    rowType === "action-card" ||
    rowType === "msg-action" ||
    rowType === "action_card";

  // Resolve body data: prefer fetched bodyRow (has body_xml/body_json),
  // fall back to the list-endpoint row (d) which may also carry them
  // if sub-agent C inlines them on the list endpoint.
  const effectiveRow = bodyRow ?? d;
  const bodyXml = effectiveRow.body_xml ?? null;
  const bodyJson = effectiveRow.body_json ?? null;

  // --- action-card path ---
  if (isActionCard) {
    const steps = bodyXml ? parseStepsFromXml(bodyXml) : [];
    if (steps.length > 0) {
      return <BodyStepsView steps={steps} />;
    }
    // No steps from body — fall through to standard choices or pending notice.
  }

  // --- option-bearing path (default for msg-escalation, decision-card, etc.) ---
  // Try body_xml / body_json from the fetched row first.
  if (bodyXml || bodyJson) {
    const fromJson = bodyJson ? parseOptionsFromJson(bodyJson) : [];
    const fromXml = bodyXml ? parseOptionsFromXml(bodyXml) : [];
    const options = fromJson.length > 0 ? fromJson : fromXml;
    if (options.length > 0) {
      return (
        <BodyOptionsView
          options={options}
          recommended={d.recommended}
          onChoose={onChoose}
          submitting={submitting}
        />
      );
    }
  }

  // --- loading state ---
  if (bodyFetchState === "loading") {
    return (
      <div className="drow-body-loading" data-testid="drow-body-loading">
        Loading body…
      </div>
    );
  }

  // --- legacy choices[] fallback ---
  // This is what rendered before S70. Covers:
  // (a) API not yet updated (sub-agent C pending) → choices from list endpoint
  // (b) Rows where choices[] has real data (label-keyed A/B/C from backend)
  if (d.choices && d.choices.length > 0) {
    return (
      <>
        <div className="drow-detail-head">Choice details</div>
        <div className="drow-choices">
          {d.choices.map((c: DecisionChoice) => (
            <div
              key={c.key}
              className={`drow-ch ${d.recommended === c.key ? "is-rec" : ""}`}
            >
              <div className="drow-ch-head">
                <span className="drow-ch-k">{c.key}</span>
                <span className="drow-ch-l">{c.label}</span>
                {d.recommended === c.key && (
                  <span className="drow-ch-tag">board rec</span>
                )}
              </div>
              <div className="drow-ch-d">{c.detail}</div>
              <div className="drow-ch-m">{c.meta}</div>
            </div>
          ))}
        </div>
      </>
    );
  }

  // --- body unavailable notice ---
  // Shown when body_xml/body_json are absent AND choices[] is empty.
  // Prevents the empty A/B/C scaffold from rendering.
  return (
    <div className="drow-body-unavailable" data-testid="drow-body-unavailable">
      {bodyFetchState === "unavailable"
        ? "Body not yet available — pending API change (sub-agent C)"
        : "No choice details available."}
    </div>
  );
}

const SLOT_KEYS = ["A", "B", "C"] as const;
export const STALE_THRESHOLD_MS = 48 * 60 * 60 * 1000;

export function isDecisionStale(filed: number, now: number = Date.now()): boolean {
  if (!filed || !Number.isFinite(filed) || filed <= 0) return false;
  return now - filed > STALE_THRESHOLD_MS;
}

function DecisionRow({ d, expanded, onToggle, onResolve, fetchDecisionBody, onDismiss }: DecisionRowProps) {
  const [otherOpen, setOtherOpen] = useState(false);
  const [otherText, setOtherText] = useState("");
  const [discussOpen, setDiscussOpen] = useState(false);
  const [discussText, setDiscussText] = useState("");
  const [problemFullyOpen, setProblemFullyOpen] = useState(false);
  const [submitting, setSubmitting] = useState<string | null>(null);
  // S70: body-fetch state — populated on first expand when fetchDecisionBody is provided.
  const [bodyRow, setBodyRow] = useState<Decision | null>(null);
  const [bodyFetchState, setBodyFetchState] = useState<"idle" | "loading" | "done" | "unavailable">("idle");
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const discussTaRef = useRef<HTMLTextAreaElement | null>(null);
  const rowId = `drow-${d.id}`;
  const detailId = `drow-detail-${d.id}`;

  useEffect(() => {
    if (otherOpen && taRef.current) taRef.current.focus();
  }, [otherOpen]);

  useEffect(() => {
    if (discussOpen && discussTaRef.current) discussTaRef.current.focus();
  }, [discussOpen]);

  // S70: fetch body on first expand (body_xml / body_json for rich rendering).
  // Fires only when fetchDecisionBody prop is provided and row just became expanded.
  const fetchBody = useCallback(async () => {
    if (!fetchDecisionBody || bodyFetchState !== "idle") return;
    setBodyFetchState("loading");
    const result = await fetchDecisionBody(d.id);
    if (result) {
      setBodyRow(result);
      setBodyFetchState("done");
    } else {
      setBodyFetchState("unavailable");
    }
  }, [fetchDecisionBody, d.id, bodyFetchState]);

  useEffect(() => {
    if (expanded && bodyFetchState === "idle" && fetchDecisionBody) {
      void fetchBody();
    }
  }, [expanded, bodyFetchState, fetchDecisionBody, fetchBody]);

  const pick = (key: string) => {
    setSubmitting(key);
    window.setTimeout(() => onResolve?.(d, key), 450);
  };
  const submitOther = () => {
    if (!otherText.trim()) return;
    setSubmitting("other");
    window.setTimeout(() => onResolve?.(d, "other", otherText.trim()), 450);
  };
  const submitDiscuss = () => {
    if (!discussText.trim()) return;
    setSubmitting("discuss");
    // Per BOARDROOM-2026-04-26-162700 v3-revert: Discuss flips ESC to
    // discussing; row drops out of active panel and surfaces in the
    // "Needs further discussion" sub-section. Optimistic-hide locally
    // to match Decide's UX; backend confirms via the resolve callback.
    window.setTimeout(() => {
      onResolve?.(d, "discuss", discussText.trim());
      setDiscussOpen(false);
      setDiscussText("");
      setSubmitting(null);
    }, 450);
  };

  const primary: (DecisionChoice | null)[] = SLOT_KEYS.map(
    (k) => d.choices.find((c) => c.key === k) ?? null,
  );
  const overflow = d.choices.filter(
    (c) => !SLOT_KEYS.includes(c.key as (typeof SLOT_KEYS)[number]),
  );

  const stale = isDecisionStale(d.filed);

  return (
    <div
      className={`drow ${expanded ? "is-open" : ""} ${submitting ? "is-submitting" : ""} ${stale ? "is-stale" : ""}`}
      data-decision-id={d.id}
      data-stale={stale ? "true" : undefined}
      id={rowId}
    >
      <div className="drow-main">
        <button
          type="button"
          className="drow-head"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={detailId}
        >
          <span className="drow-mark" aria-hidden="true">
            {expanded ? "▾" : "▸"}
          </span>
          <span className="drow-text">
            <span className="drow-title">{d.heading || d.id}</span>
            <span className="drow-id">{d.id}</span>
          </span>
          <span className="drow-origin">{d.origin}</span>
          <span className="drow-filed">
            {stale && (
              <span
                className="drow-stale-badge"
                aria-label="Awaiting more than 48 hours"
                title="This decision has been awaiting ratify for more than 48 hours"
              >
                🕒 awaiting &gt;48h
              </span>
            )}
            <Rel t={d.filed} />
          </span>
        </button>
        <div
          className="drow-actions drow-actions-grid"
          onClick={(e) => e.stopPropagation()}
        >
          {primary.map((c, i) =>
            c ? (
              <button
                key={SLOT_KEYS[i]}
                type="button"
                className={`drow-opt ${d.recommended === c.key ? "is-rec" : ""} ${submitting === c.key ? "is-busy" : ""}`}
                onClick={() => pick(c.key)}
                disabled={!!submitting}
                title={c.detail}
                aria-label={`Ratify ${c.label}`}
              >
                <span className="drow-opt-k">{c.key}</span>
                <span className="drow-opt-l">{c.label}</span>
              </button>
            ) : (
              <span
                key={SLOT_KEYS[i]}
                className="drow-opt drow-opt-empty"
                aria-hidden="true"
              >
                <span className="drow-opt-k">{SLOT_KEYS[i]}</span>
                <span className="drow-opt-l">—</span>
              </span>
            ),
          )}
          {overflow.length > 0 ? (
            <button
              type="button"
              className="drow-opt drow-more"
              title={overflow.map((o) => `${o.key}: ${o.label}`).join(" · ")}
              aria-label={`${overflow.length} additional options`}
            >
              +{overflow.length} more
            </button>
          ) : (
            <span
              className="drow-opt drow-opt-empty drow-more-empty"
              aria-hidden="true"
            >
              —
            </span>
          )}
          <button
            type="button"
            className={`drow-opt drow-other ${otherOpen ? "on" : ""}`}
            onClick={() => setOtherOpen((x) => !x)}
            aria-expanded={otherOpen}
            aria-controls={`drow-other-${d.id}`}
          >
            <span className="drow-opt-l">Other…</span>
          </button>
          <button
            type="button"
            className={`drow-opt drow-discuss ${discussOpen ? "on" : ""}`}
            onClick={() => setDiscussOpen((x) => !x)}
            disabled={!!submitting}
            aria-expanded={discussOpen}
            aria-controls={`drow-discuss-${d.id}`}
            title="Send a question or comment without picking a choice; ESC stays open"
          >
            <span className="drow-opt-l">Discuss…</span>
          </button>
          {onDismiss && (
            <button
              type="button"
              className="drow-opt drow-dismiss"
              onClick={() => onDismiss(d)}
              disabled={!!submitting}
              title="Clear this card — no reason required. It stops appearing everywhere (superseded), it is not a decision."
              aria-label={`Dismiss ${d.id}`}
            >
              <span className="drow-opt-l">Dismiss</span>
            </button>
          )}
        </div>
      </div>

      {d.problem && (
        <div className="drow-summary" aria-label="problem description">
          <p
            className={`drow-summary-text ${problemFullyOpen ? "is-full" : "is-clamped"}`}
            data-testid={`drow-summary-text-${d.id}`}
          >
            {d.problem}
          </p>
          <button
            type="button"
            className="drow-summary-toggle"
            onClick={() => setProblemFullyOpen((x) => !x)}
            aria-expanded={problemFullyOpen}
            aria-controls={`drow-summary-text-${d.id}`}
          >
            {problemFullyOpen ? "show less" : "show more"}
          </button>
        </div>
      )}

      {expanded && (
        <div className="drow-detail" id={detailId} role="region" aria-labelledby={rowId}>
          {/* S70: render-by-subtype — branch on type field (BOARDROOM-2026-05-05-071740-480).
              Priority: parsed body_xml/body_json from fetchDecisionBody (sub-agent C API
              contract: GET /api/decisions/{id}?include_body=true) > choices[] from the list
              endpoint > legacy Choice details scaffold. */}
          {_renderBodyContent(
            d,
            bodyRow,
            bodyFetchState,
            pick,
            submitting,
          )}
          {d.blocking.length > 0 && (
            <div className="drow-blocking">
              blocking:{" "}
              {d.blocking.map((b) => (
                <code key={b}>{b}</code>
              ))}
            </div>
          )}
          {/* WU-08: Previous decisions + Chain trail + OTHER follow-up */}
          <PreviousDecisions history={d.ratify_history ?? []} />
          <OtherFollowupContext history={d.ratify_history ?? []} />
          <ChainTrail links={d.chain_links ?? []} />
        </div>
      )}

      {otherOpen && (
        <div
          className="drow-other-panel"
          id={`drow-other-${d.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <label className="drow-ta-label" htmlFor={`drow-ta-${d.id}`}>
            Write your own direction to boardroom-laptop
          </label>
          <textarea
            ref={taRef}
            id={`drow-ta-${d.id}`}
            className="drow-ta"
            placeholder="This dispatches as a reply — not one of the proposed options."
            value={otherText}
            onChange={(e) => setOtherText(e.target.value)}
            rows={3}
          />
          <div className="drow-ta-foot">
            <button
              type="button"
              className="drow-ta-cancel"
              onClick={() => {
                setOtherOpen(false);
                setOtherText("");
              }}
            >
              cancel
            </button>
            <button
              type="button"
              className="drow-ta-send"
              disabled={!otherText.trim() || !!submitting}
              onClick={submitOther}
            >
              {submitting === "other" ? "sending…" : "dispatch → boardroom"}
            </button>
          </div>
        </div>
      )}

      {discussOpen && (
        <div
          className="drow-discuss-panel drow-other-panel"
          id={`drow-discuss-${d.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          <label className="drow-ta-label" htmlFor={`drow-discuss-ta-${d.id}`}>
            Send a thought to boardroom — parks ESC in "Needs further discussion"
          </label>
          <textarea
            ref={discussTaRef}
            id={`drow-discuss-ta-${d.id}`}
            className="drow-ta"
            placeholder="Send a thought to boardroom — boardroom will pick this up via session interface or phone. ESC parks in 'Needs further discussion' until a choice is made."
            value={discussText}
            onChange={(e) => setDiscussText(e.target.value)}
            rows={3}
          />
          <div className="drow-ta-foot">
            <button
              type="button"
              className="drow-ta-cancel"
              onClick={() => {
                setDiscussOpen(false);
                setDiscussText("");
              }}
            >
              cancel
            </button>
            <button
              type="button"
              className="drow-ta-send"
              disabled={!discussText.trim() || !!submitting}
              onClick={submitDiscuss}
            >
              {submitting === "discuss" ? "sending…" : "send → boardroom"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export type RatifyAction = {
  action: "decide" | "discuss";
  chosen_key?: string;
  chosen_other_text?: string;
  reasoning?: string;
  discuss_reason?: string;
};

export default function PanelDecisions({
  decisions,
  onRatify,
  recentlyDecided = [],
  needsDiscussion = [],
  fetchDecisionBody,
  onDismiss,
}: {
  decisions: Decision[];
  onRatify?: (ref: string, payload: RatifyAction) => Promise<void>;
  recentlyDecided?: Decision[];
  needsDiscussion?: Decision[];
  /** fetchDecisionBody: calls GET /api/decisions/{id}?include_body=true.
   *  Optional — when absent, expanded rows fall back to legacy choices[].
   *  Sub-agent C is implementing the backend endpoint; this prop wires the
   *  frontend to it defensively (missing/404 = graceful degradation). */
  fetchDecisionBody?: (id: string) => Promise<Decision | null>;
  /** BOARDROOM-2026-08-08-124017 step 2: POST /api/cards/{ref}/dismiss.
   *  Optional — when absent, no Dismiss button renders (backward compat). */
  onDismiss?: (ref: string) => Promise<void>;
}) {
  const [list, setList] = useState<Decision[]>(decisions);
  const [openId, setOpenId] = useState<string | null>(null);
  const [ratifyError, setRatifyError] = useState<string | null>(null);
  const [recentOpen, setRecentOpen] = useState(false);
  const [discussOpen, setDiscussOpen] = useState(false);

  useEffect(() => {
    setList(decisions);
  }, [decisions]);

  const dismiss = (d: Decision) => {
    // Same optimistic-hide pattern as resolve() — dismiss is a lifecycle
    // action, not a decision, so it doesn't go through onRatify.
    setList((xs) => xs.filter((x) => x.id !== d.id));
    if (openId === d.id) setOpenId(null);
    if (!onDismiss) return;
    onDismiss(d.id).catch((err: unknown) => {
      setRatifyError(err instanceof Error ? err.message : String(err));
    });
  };

  const resolve = (d: Decision, key: string, text?: string) => {
    // Per v3-revert: BOTH decide AND discuss optimistic-hide. Decide flips
    // ESC to resolved; discuss flips to discussing. Either way the row
    // drops out of the active panel; the next refetch confirms (or, on
    // failure, restores the row because its decision_state didn't flip).
    setList((xs) => xs.filter((x) => x.id !== d.id));
    if (openId === d.id) setOpenId(null);
    if (!onRatify) return;
    let payload: RatifyAction;
    if (key === "discuss") {
      payload = { action: "discuss", discuss_reason: text };
    } else if (key === "other") {
      payload = { action: "decide", chosen_key: "OTHER", chosen_other_text: text };
    } else {
      payload = { action: "decide", chosen_key: key };
    }
    onRatify(d.id, payload).catch((err: unknown) => {
      setRatifyError(err instanceof Error ? err.message : String(err));
    });
  };

  return (
    <section
      className={`panel panel-a ${list.length === 0 ? "is-empty" : "is-populated"}`}
      aria-labelledby="panel-a-title"
    >
      <header className="panel-head">
        <h2 className="panel-title" id="panel-a-title">
          Duncan decisions
        </h2>
        <span className="panel-meta">
          <span className="panel-count">{list.length}</span>
          <span className="panel-sub">
            awaiting ratify · polled every 15s
          </span>
        </span>
      </header>
      <div className="panel-body">
        {ratifyError && (
          <div
            className="panel-empty"
            style={{ color: "#fca5a5", fontSize: 11, padding: "6px 18px" }}
            role="alert"
          >
            Ratify failed: {ratifyError}
          </div>
        )}
        {list.length === 0 && (
          <div className="panel-empty">No decisions pending.</div>
        )}
        {list.map((d) => (
          <DecisionRow
            key={d.id}
            d={d}
            expanded={openId === d.id}
            onToggle={() => setOpenId((x) => (x === d.id ? null : d.id))}
            onResolve={resolve}
            fetchDecisionBody={fetchDecisionBody}
            onDismiss={onDismiss ? dismiss : undefined}
          />
        ))}
        {needsDiscussion.length > 0 && (
          <div className={`drow-recent drow-needs-discussion ${discussOpen ? "is-open" : ""}`}>
            <button
              type="button"
              className="drow-recent-toggle"
              onClick={() => setDiscussOpen((v) => !v)}
              aria-expanded={discussOpen}
              aria-controls="drow-needs-discussion-list"
            >
              <span className="drow-recent-mark" aria-hidden="true">
                {discussOpen ? "▾" : "▸"}
              </span>
              <span className="drow-recent-label">Needs further discussion</span>
              <span className="drow-recent-count">{needsDiscussion.length}</span>
            </button>
            {discussOpen && (
              <ul
                id="drow-needs-discussion-list"
                className="drow-recent-list"
                aria-label="Needs further discussion"
              >
                {needsDiscussion.map((nd) => (
                  <li key={nd.id} className="drow-recent-row">
                    <span className="drow-recent-title">
                      {nd.heading || nd.id}
                    </span>
                    <span className="drow-recent-key">DISCUSSING</span>
                    <span className="drow-recent-when">
                      {nd.filed
                        ? new Date(nd.filed).toLocaleString()
                        : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
        {recentlyDecided.length > 0 && (
          <div className={`drow-recent ${recentOpen ? "is-open" : ""}`}>
            <button
              type="button"
              className="drow-recent-toggle"
              onClick={() => setRecentOpen((v) => !v)}
              aria-expanded={recentOpen}
              aria-controls="drow-recent-list"
            >
              <span className="drow-recent-mark" aria-hidden="true">
                {recentOpen ? "▾" : "▸"}
              </span>
              <span className="drow-recent-label">Recently decided (last 24h)</span>
              <span className="drow-recent-count">{recentlyDecided.length}</span>
            </button>
            {recentOpen && (
              <ul
                id="drow-recent-list"
                className="drow-recent-list"
                aria-label="Recently decided"
              >
                {recentlyDecided.map((rd) => (
                  <li key={rd.id} className="drow-recent-row">
                    <span className="drow-recent-title">
                      {rd.heading || rd.id}
                    </span>
                    <span className="drow-recent-key">
                      {rd.chosen_key || "—"}
                    </span>
                    <span className="drow-recent-when">
                      {rd.decided_at
                        ? new Date(rd.decided_at).toLocaleString()
                        : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
